'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { applySelectionDiff, Badge, Button, DataTable, DropdownMenu, HighlightedText, IconButton, TableCellTwoLines, Tooltip } from '@/shared/components/ui';
import type { BulkAction, ColumnDef, SortState } from '@/shared/components/ui/common/DataTable';
import { useAppSelector } from '@/store/hooks';
import { formatCustomFieldValue, selectColunasSelecionadasOrdenadas } from '@/features/negociacaoColunas';
import { SITUACAO_KEY_INTERVENCAO_HUMANA } from '../constants/situacaoKeys';
import * as SubframeCore from '@subframe/core';
import { FeatherAlertCircle, FeatherArchive, FeatherFlame, FeatherMoreVertical, FeatherPause, FeatherPlay, FeatherPlus, FeatherRotateCcw, FeatherSearch, FeatherSnowflake, FeatherThermometer, FeatherXCircle } from '@subframe/core';
import type { NegociacaoPeriodoCampo } from '../types';
import type { EmptyStateCriacao, NegociacaoListRow, NegociacoesSortConfig, NegociacoesSortKey } from '../types/negociacoesUiTypes';
import { getTemperatureMeta, rotuloDaColunaDeData, statusPillClassMap, valorDaColunaDeData } from '../utils';
interface NegociacoesTableProps {
  rows: NegociacaoListRow[];
  isLoading: boolean;
  error: string | null;
  totalResults: number;
  cpfsUnicos?: number | null;
  totalPages: number;
  page: number;
  setPage: (value: number) => void;
  hasSearchOrFilters: boolean;
  onClearSearchAndFilters: () => void;
  onStartNegotiacao?: () => void;
  emptyStateCriacao?: EmptyStateCriacao;
  sortConfig: NegociacoesSortConfig;
  handleSort: (key: NegociacoesSortKey) => void;
  selectedIds: Set<string>;
  toggleSelect: (id: string) => void;
  onPauseClick: (id: string) => void;
  onResumeClick: (id: string) => void;
  onArchiveClick: (id: string) => void;
  onUnarchiveClick: (id: string) => void;
  onCancelStartClick: (id: string) => void;
  onBulkPause: () => void;
  onBulkResume: () => void;
  onBulkArchive: () => void;
  onBulkUnarchive: () => void;
  onBulkClose: () => void;
  showArchiveActions?: boolean;
  showUnarchiveActions?: boolean;
  onRetryFetch: () => void;
  onRowClick: (id: string) => void;
  onDetailsClick: (id: string) => void;
  onCloseClick: (id: string) => void;
  searchQuery: string;
  campoPeriodo: NegociacaoPeriodoCampo | null;
  legendaPeriodo: string;
}
const getTemperatureIcon = (temperature: NegociacaoListRow['temperature']) => {
  const meta = getTemperatureMeta(temperature);
  if (meta.iconType === 'flame') return <FeatherFlame className="h-4 w-4" />;
  if (meta.iconType === 'thermometer') return <FeatherThermometer className="h-4 w-4" />;
  return <FeatherSnowflake className="h-4 w-4" />;
};
const isRowConcluded = (statusValue: NegociacaoListRow['statusValue']) => statusValue === 'AGREEMENT_CLOSED' || statusValue === 'NO_AGREEMENT' || statusValue === 'CLOSED';
export function NegociacoesTable(t0) {
  const $ = _c(134);
  const {
    rows,
    isLoading,
    error,
    totalResults,
    cpfsUnicos,
    totalPages,
    page,
    setPage,
    hasSearchOrFilters,
    onClearSearchAndFilters,
    onStartNegotiacao,
    emptyStateCriacao,
    sortConfig,
    handleSort,
    selectedIds,
    toggleSelect,
    onPauseClick,
    onResumeClick,
    onArchiveClick,
    onUnarchiveClick,
    onCancelStartClick,
    onBulkPause,
    onBulkResume,
    onBulkArchive,
    onBulkUnarchive,
    onBulkClose,
    showArchiveActions: t1,
    showUnarchiveActions: t2,
    onRetryFetch,
    onRowClick,
    onDetailsClick,
    onCloseClick,
    searchQuery,
    campoPeriodo,
    legendaPeriodo
  } = t0;
  const showArchiveActions = t1 === undefined ? true : t1;
  const showUnarchiveActions = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== sortConfig.direction || $[1] !== sortConfig.key) {
    t3 = {
      key: sortConfig.key,
      direction: sortConfig.direction
    };
    $[0] = sortConfig.direction;
    $[1] = sortConfig.key;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const sort = t3;
  let t4;
  if ($[3] !== handleSort) {
    t4 = next => {
      handleSort(next.key as NegociacoesSortKey);
    };
    $[3] = handleSort;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const onSortChange = t4;
  let t5;
  if ($[5] !== selectedIds || $[6] !== toggleSelect) {
    t5 = next_0 => applySelectionDiff(selectedIds, next_0, toggleSelect);
    $[5] = selectedIds;
    $[6] = toggleSelect;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const onSelectionChange = t5;
  let t6;
  if ($[8] !== rows || $[9] !== selectedIds) {
    let t7;
    if ($[11] !== selectedIds) {
      t7 = r => selectedIds.has(r.id);
      $[11] = selectedIds;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    t6 = rows.filter(t7);
    $[8] = rows;
    $[9] = selectedIds;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const selectedRows = t6;
  let t7;
  if ($[13] !== onBulkPause) {
    t7 = () => onBulkPause();
    $[13] = onBulkPause;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== selectedRows) {
    t8 = () => !selectedRows.some(_temp);
    $[15] = selectedRows;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = {
      id: "pause",
      label: "Pausar",
      onClick: t7,
      disabled: t8
    };
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== onBulkResume) {
    t10 = () => onBulkResume();
    $[20] = onBulkResume;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== selectedRows) {
    t11 = () => !selectedRows.some(_temp2);
    $[22] = selectedRows;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = {
      id: "resume",
      label: "Reiniciar",
      onClick: t10,
      disabled: t11
    };
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let actions;
  if ($[27] !== onBulkArchive || $[28] !== onBulkClose || $[29] !== onBulkUnarchive || $[30] !== selectedRows || $[31] !== showArchiveActions || $[32] !== showUnarchiveActions || $[33] !== t12 || $[34] !== t9) {
    actions = [t9, t12];
    if (showArchiveActions) {
      let t13;
      if ($[36] !== onBulkArchive) {
        t13 = {
          id: "archive",
          label: "Arquivar",
          onClick: () => onBulkArchive(),
          disabled: _temp3
        };
        $[36] = onBulkArchive;
        $[37] = t13;
      } else {
        t13 = $[37];
      }
      actions.push(t13);
    }
    if (showUnarchiveActions) {
      let t13;
      if ($[38] !== onBulkUnarchive) {
        t13 = {
          id: "unarchive",
          label: "Desarquivar",
          onClick: () => onBulkUnarchive(),
          disabled: _temp4
        };
        $[38] = onBulkUnarchive;
        $[39] = t13;
      } else {
        t13 = $[39];
      }
      actions.push(t13);
    }
    let t13;
    if ($[40] !== onBulkClose) {
      t13 = () => onBulkClose();
      $[40] = onBulkClose;
      $[41] = t13;
    } else {
      t13 = $[41];
    }
    let t14;
    if ($[42] !== selectedRows) {
      t14 = () => !selectedRows.some(_temp5);
      $[42] = selectedRows;
      $[43] = t14;
    } else {
      t14 = $[43];
    }
    let t15;
    if ($[44] !== t13 || $[45] !== t14) {
      t15 = {
        id: "close",
        label: "Encerrar",
        variant: "destructive-secondary",
        onClick: t13,
        disabled: t14
      };
      $[44] = t13;
      $[45] = t14;
      $[46] = t15;
    } else {
      t15 = $[46];
    }
    actions.push(t15);
    $[27] = onBulkArchive;
    $[28] = onBulkClose;
    $[29] = onBulkUnarchive;
    $[30] = selectedRows;
    $[31] = showArchiveActions;
    $[32] = showUnarchiveActions;
    $[33] = t12;
    $[34] = t9;
    $[35] = actions;
  } else {
    actions = $[35];
  }
  const bulkActions = actions;
  const colunasExtras = useAppSelector(selectColunasSelecionadasOrdenadas);
  let t13;
  if ($[47] !== searchQuery) {
    t13 = {
      id: "client",
      header: "Cliente",
      sortable: true,
      width: "w-[25%]",
      minWidth: "min-w-[200px]",
      cell: neg => <TableCellTwoLines title={<span className="text-body-sm-semibold"><HighlightedText text={neg.client || "N/A"} query={searchQuery} /></span>} subtitle={<HighlightedText text={neg.numeroNegociacao || neg.negotiationName || "N/A"} query={searchQuery} />} />
    };
    $[47] = searchQuery;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== campoPeriodo) {
    t14 = campoPeriodo ? <span className="text-brand-600">{rotuloDaColunaDeData(campoPeriodo)}</span> : "Criada em";
    $[49] = campoPeriodo;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  const t15 = campoPeriodo !== "ultimaInteracao";
  let t16;
  if ($[51] !== campoPeriodo) {
    t16 = neg_0 => <span className="text-caption font-caption text-subtext-color whitespace-nowrap">{valorDaColunaDeData(neg_0, campoPeriodo)}</span>;
    $[51] = campoPeriodo;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== t14 || $[54] !== t15 || $[55] !== t16) {
    t17 = {
      id: "createdAt",
      header: t14,
      sortable: t15,
      width: "w-[15%]",
      minWidth: "min-w-[140px]",
      cell: t16
    };
    $[53] = t14;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      id: "type",
      header: "Tipo",
      sortable: true,
      width: "w-[8%]",
      cell: _temp6
    };
    t19 = {
      id: "status",
      header: "Status",
      sortable: true,
      width: "w-[12%]",
      cell: _temp7
    };
    t20 = {
      id: "situacao",
      header: "Situa\xE7\xE3o",
      sortable: false,
      width: "w-[14%]",
      cell: _temp9
    };
    t21 = {
      id: "temperature",
      header: "Temperatura",
      sortable: true,
      width: "w-[12%]",
      cell: _temp10
    };
    $[57] = t18;
    $[58] = t19;
    $[59] = t20;
    $[60] = t21;
  } else {
    t18 = $[57];
    t19 = $[58];
    t20 = $[59];
    t21 = $[60];
  }
  let t22;
  if ($[61] !== colunasExtras || $[62] !== onArchiveClick || $[63] !== onCancelStartClick || $[64] !== onCloseClick || $[65] !== onDetailsClick || $[66] !== onPauseClick || $[67] !== onResumeClick || $[68] !== onUnarchiveClick || $[69] !== showArchiveActions || $[70] !== showUnarchiveActions || $[71] !== t13 || $[72] !== t17) {
    let t23;
    if ($[74] !== onArchiveClick || $[75] !== onCancelStartClick || $[76] !== onCloseClick || $[77] !== onDetailsClick || $[78] !== onPauseClick || $[79] !== onResumeClick || $[80] !== onUnarchiveClick || $[81] !== showArchiveActions || $[82] !== showUnarchiveActions) {
      t23 = {
        id: "actions",
        header: "A\xE7\xF5es",
        align: "right",
        width: "w-[10%]",
        cell: neg_6 => {
          const isArchived = neg_6.isArchived;
          const concluded = isRowConcluded(neg_6.statusValue);
          const isFailed = neg_6.statusValue === "FAILED";
          const isScheduled = neg_6.statusValue === "SCHEDULED";
          const canPauseOrResume = !isArchived && !concluded && !isFailed;
          return <div className="flex items-center justify-end gap-2" onClick={_temp12}>{neg_6.needsAction ? <Button size="small" variant="brand-primary" onClick={event => {
              event.stopPropagation();
              onDetailsClick(neg_6.id);
            }}>Resolver</Button> : <Button variant="neutral-tertiary" size="small" className="text-brand-600 font-body-bold text-caption" onClick={event_0 => {
              event_0.stopPropagation();
              onDetailsClick(neg_6.id);
            }}>Ver detalhes</Button>}<SubframeCore.DropdownMenu.Root><SubframeCore.DropdownMenu.Trigger asChild={true}><IconButton variant="neutral-tertiary" icon={<FeatherMoreVertical />} size="small" /></SubframeCore.DropdownMenu.Trigger><SubframeCore.DropdownMenu.Portal><SubframeCore.DropdownMenu.Content side="bottom" align="end" sideOffset={8} className="animate-fade-in"><DropdownMenu>{isScheduled ? <DropdownMenu.DropdownItem icon={<FeatherXCircle />} className="text-error-600" onClick={() => onCancelStartClick(neg_6.id)}>Cancelar agendamento</DropdownMenu.DropdownItem> : null}{canPauseOrResume ? neg_6.isPaused ? <DropdownMenu.DropdownItem icon={<FeatherPlay />} onClick={() => onResumeClick(neg_6.id)}>Reiniciar negociação</DropdownMenu.DropdownItem> : <DropdownMenu.DropdownItem icon={<FeatherPause />} onClick={() => onPauseClick(neg_6.id)}>Pausar negociação</DropdownMenu.DropdownItem> : null}{showArchiveActions ? <><DropdownMenu.DropdownDivider />{!concluded && <DropdownMenu.DropdownItem icon={<FeatherArchive />} className="text-error-600" onClick={() => onCloseClick(neg_6.id)}>Encerrar negociação</DropdownMenu.DropdownItem>}<DropdownMenu.DropdownItem icon={<FeatherArchive />} onClick={() => onArchiveClick(neg_6.id)}>Arquivar negociação</DropdownMenu.DropdownItem></> : null}{showUnarchiveActions ? <><DropdownMenu.DropdownDivider /><DropdownMenu.DropdownItem icon={<FeatherRotateCcw />} onClick={() => onUnarchiveClick(neg_6.id)}>Desarquivar negociação</DropdownMenu.DropdownItem></> : null}</DropdownMenu></SubframeCore.DropdownMenu.Content></SubframeCore.DropdownMenu.Portal></SubframeCore.DropdownMenu.Root></div>;
        }
      };
      $[74] = onArchiveClick;
      $[75] = onCancelStartClick;
      $[76] = onCloseClick;
      $[77] = onDetailsClick;
      $[78] = onPauseClick;
      $[79] = onResumeClick;
      $[80] = onUnarchiveClick;
      $[81] = showArchiveActions;
      $[82] = showUnarchiveActions;
      $[83] = t23;
    } else {
      t23 = $[83];
    }
    t22 = [t13, t17, t18, t19, t20, t21, ...colunasExtras.map(_temp11), t23];
    $[61] = colunasExtras;
    $[62] = onArchiveClick;
    $[63] = onCancelStartClick;
    $[64] = onCloseClick;
    $[65] = onDetailsClick;
    $[66] = onPauseClick;
    $[67] = onResumeClick;
    $[68] = onUnarchiveClick;
    $[69] = showArchiveActions;
    $[70] = showUnarchiveActions;
    $[71] = t13;
    $[72] = t17;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  const columns = t22;
  let t23;
  if ($[84] !== totalResults) {
    t23 = <Badge variant="neutral">{totalResults} resultados</Badge>;
    $[84] = totalResults;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  let t24;
  if ($[86] !== cpfsUnicos) {
    t24 = cpfsUnicos != null && <Badge variant="neutral">{cpfsUnicos.toLocaleString("pt-BR")} CPFs únicos</Badge>;
    $[86] = cpfsUnicos;
    $[87] = t24;
  } else {
    t24 = $[87];
  }
  let t25;
  if ($[88] !== legendaPeriodo) {
    t25 = legendaPeriodo && <span className="text-caption font-caption text-subtext-color">{legendaPeriodo}</span>;
    $[88] = legendaPeriodo;
    $[89] = t25;
  } else {
    t25 = $[89];
  }
  let t26;
  if ($[90] !== t23 || $[91] !== t24 || $[92] !== t25) {
    t26 = {
      title: "Negocia\xE7\xF5es",
      badge: <div className="flex items-center gap-2">{t23}{t24}{t25}</div>
    };
    $[90] = t23;
    $[91] = t24;
    $[92] = t25;
    $[93] = t26;
  } else {
    t26 = $[93];
  }
  let t27;
  if ($[94] !== page || $[95] !== totalPages || $[96] !== totalResults) {
    t27 = {
      page,
      totalPages,
      totalItems: totalResults
    };
    $[94] = page;
    $[95] = totalPages;
    $[96] = totalResults;
    $[97] = t27;
  } else {
    t27 = $[97];
  }
  let t28;
  if ($[98] !== error || $[99] !== onRetryFetch) {
    t28 = error ? {
      icon: <FeatherAlertCircle className="text-error-600" />,
      title: "Erro ao carregar negocia\xE7\xF5es",
      description: error,
      retryAction: onRetryFetch,
      retryLabel: "Tentar novamente"
    } : null;
    $[98] = error;
    $[99] = onRetryFetch;
    $[100] = t28;
  } else {
    t28 = $[100];
  }
  let t29;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <FeatherSearch />;
    $[101] = t29;
  } else {
    t29 = $[101];
  }
  const t30 = emptyStateCriacao?.title ?? "Comece sua primeira negocia\xE7\xE3o";
  const t31 = emptyStateCriacao?.description ?? "Voc\xEA ainda n\xE3o possui negocia\xE7\xF5es. Clique em 'Iniciar negocia\xE7\xE3o' para come\xE7ar.";
  let t32;
  if ($[102] !== emptyStateCriacao || $[103] !== onStartNegotiacao) {
    t32 = emptyStateCriacao ? <Button variant="brand-secondary" onClick={emptyStateCriacao.onAction}>{emptyStateCriacao.actionLabel}</Button> : onStartNegotiacao ? <Button variant="brand-primary" onClick={onStartNegotiacao} icon={<FeatherPlus />}>Iniciar negociação</Button> : undefined;
    $[102] = emptyStateCriacao;
    $[103] = onStartNegotiacao;
    $[104] = t32;
  } else {
    t32 = $[104];
  }
  let t33;
  if ($[105] !== t30 || $[106] !== t31 || $[107] !== t32) {
    t33 = {
      icon: t29,
      title: t30,
      description: t31,
      action: t32
    };
    $[105] = t30;
    $[106] = t31;
    $[107] = t32;
    $[108] = t33;
  } else {
    t33 = $[108];
  }
  let t34;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <FeatherSearch />;
    $[109] = t34;
  } else {
    t34 = $[109];
  }
  const t35 = legendaPeriodo ? `Nenhuma negociação encontrada com os filtros selecionados (${legendaPeriodo}).` : "Nenhuma negocia\xE7\xE3o encontrada com os filtros selecionados.";
  let t36;
  if ($[110] !== onClearSearchAndFilters) {
    t36 = <Button variant="neutral-secondary" onClick={onClearSearchAndFilters}>Limpar filtros</Button>;
    $[110] = onClearSearchAndFilters;
    $[111] = t36;
  } else {
    t36 = $[111];
  }
  let t37;
  if ($[112] !== t35 || $[113] !== t36) {
    t37 = {
      icon: t34,
      title: "Nenhuma negocia\xE7\xE3o encontrada",
      description: t35,
      action: t36
    };
    $[112] = t35;
    $[113] = t36;
    $[114] = t37;
  } else {
    t37 = $[114];
  }
  let t38;
  if ($[115] !== onRowClick) {
    t38 = neg_8 => onRowClick(neg_8.id);
    $[115] = onRowClick;
    $[116] = t38;
  } else {
    t38 = $[116];
  }
  let t39;
  if ($[117] !== bulkActions || $[118] !== columns || $[119] !== hasSearchOrFilters || $[120] !== isLoading || $[121] !== onSelectionChange || $[122] !== onSortChange || $[123] !== rows || $[124] !== selectedIds || $[125] !== setPage || $[126] !== sort || $[127] !== t26 || $[128] !== t27 || $[129] !== t28 || $[130] !== t33 || $[131] !== t37 || $[132] !== t38) {
    t39 = <DataTable data={rows} columns={columns} getRowId={_temp13} card={t26} sort={sort} onSortChange={onSortChange} selectionMode="multiple" selectedIds={selectedIds} onSelectionChange={onSelectionChange} pagination={t27} onPageChange={setPage} isLoading={isLoading} error={t28} emptyState={t33} emptyFilterState={t37} hasActiveFilters={hasSearchOrFilters} onRowClick={t38} bulkActions={bulkActions} bulkActionsPosition="header" />;
    $[117] = bulkActions;
    $[118] = columns;
    $[119] = hasSearchOrFilters;
    $[120] = isLoading;
    $[121] = onSelectionChange;
    $[122] = onSortChange;
    $[123] = rows;
    $[124] = selectedIds;
    $[125] = setPage;
    $[126] = sort;
    $[127] = t26;
    $[128] = t27;
    $[129] = t28;
    $[130] = t33;
    $[131] = t37;
    $[132] = t38;
    $[133] = t39;
  } else {
    t39 = $[133];
  }
  return t39;
}
function _temp13(neg_7) {
  return neg_7.id;
}
function _temp12(e) {
  return e.stopPropagation();
}
function _temp11(coluna) {
  return {
    id: `cf-${coluna.fieldId}`,
    header: coluna.label,
    sortable: false,
    minWidth: "min-w-[140px]",
    cell: neg_5 => <span className="text-default-font text-[12px] leading-none font-normal whitespace-nowrap">{formatCustomFieldValue(neg_5.customFieldValues?.[coluna.fieldId], coluna.type)}</span>
  };
}
function _temp10(neg_4) {
  const meta = getTemperatureMeta(neg_4.temperature);
  return <div className="flex items-center gap-1.5"><SubframeCore.Tooltip.Provider><SubframeCore.Tooltip.Root><SubframeCore.Tooltip.Trigger asChild={true}><span className={meta.colorClass}>{getTemperatureIcon(neg_4.temperature)}</span></SubframeCore.Tooltip.Trigger><SubframeCore.Tooltip.Portal><SubframeCore.Tooltip.Content side="top" align="center" sideOffset={4}><Tooltip>{meta.description}</Tooltip></SubframeCore.Tooltip.Content></SubframeCore.Tooltip.Portal></SubframeCore.Tooltip.Root></SubframeCore.Tooltip.Provider><span className={`text-caption font-medium ${meta.colorClass}`}>{neg_4.temperature}</span></div>;
}
function _temp9(neg_3) {
  return neg_3.situacoes.length > 0 ? <div className="flex flex-wrap gap-1">{neg_3.situacoes.map(_temp8)}</div> : <span className="text-subtext-color text-[12px] leading-none font-normal">—</span>;
}
function _temp8(situacao) {
  return <span key={situacao.key} className="neg-situacao-pill">{situacao.key === SITUACAO_KEY_INTERVENCAO_HUMANA && <span className="neg-situacao-pill__dot" />}{situacao.label}</span>;
}
function _temp7(neg_2) {
  return <span className={`neg-status-pill ${neg_2.isArchived ? statusPillClassMap.ARCHIVED : statusPillClassMap[neg_2.statusValue]}`}>{neg_2.status}</span>;
}
function _temp6(neg_1) {
  return <span className="text-default-font text-[12px] leading-none font-normal">{neg_1.type}</span>;
}
function _temp5(r_2) {
  return r_2.statusValue !== "CLOSED" && r_2.statusValue !== "AGREEMENT_CLOSED" && r_2.statusValue !== "NO_AGREEMENT" && r_2.statusValue !== "ARCHIVED";
}
function _temp4(ids_0) {
  return ids_0.length === 0;
}
function _temp3(ids) {
  return ids.length === 0;
}
function _temp2(r_1) {
  return r_1.statusValue === "PAUSED";
}
function _temp(r_0) {
  return r_0.statusValue !== "PAUSED" && r_0.statusValue !== "AGREEMENT_CLOSED" && r_0.statusValue !== "NO_AGREEMENT" && !r_0.isArchived;
}